import { formatMoney } from '@theme/money-formatting';

/*
 * Fisher's Holiday 2026 — holiday product page helpers (sections/holiday-pdp.liquid).
 *
 * <fh-pdp-kit> is an empty marker element inside <everyday-pdp>. It never
 * touches selection, price or the product form — assets/everyday-pdp.js owns
 * those — it only reads them and keeps the holiday extras in step:
 *
 *   - free-shipping helper ([data-fh-ship]): names the cheapest way over the
 *     line, using the same rules and message templates as
 *     snippets/fh-pdp-ship.liquid: "Make it 2" gets a one-tap button, the
 *     stocking stuffer gets an "Unlocks free shipping" ribbon on its card
 *   - stocking-stuffer card ([data-fh-addon]): keeps the one-tap button's
 *     variant id and price on the picked flavor (everyday-pdp.js adds it)
 *   - "Also comes in" links (a[data-fh-ladder]): carry the current flavor/design
 *     to the other sizes (separate products) as ?flavor=caramel&design=beach-snowman
 *   - "Ships free" pill next to the price
 *
 * Inputs: the form's variant input (updated synchronously on every chip tap),
 * the theme's variant:update / quantity-selector:update / cart:update events,
 * and the JSON payloads Liquid renders (script[data-pdp-data], script[data-fh-pdp]).
 */

class FhPdpKit extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('everyday-pdp');
    if (!this.root) return;
    this.data = this.#readJson('script[data-pdp-data]');
    this.cfg = this.#readJson('script[data-fh-pdp]');
    if (!this.data || !this.cfg) return;

    this.cart = Number(this.data.cartSubtotal) || 0;
    this.qty = 1;
    // after Add to cart the selection is already inside the cart total: count the cart alone
    // until the shopper actually picks a different variant (or changes the quantity)
    this.justAdded = false;
    this.addedVariantId = null;
    this.ship = this.root.querySelector('[data-fh-ship]');
    this.addon = this.root.querySelector('[data-fh-addon]');

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    // a chip tap updates the form's variant input synchronously; variant:update follows after a fetch
    this.root.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('.pdp-chip')) {
          queueMicrotask(() => this.#onSelection());
        }
      },
      { signal }
    );
    this.root.addEventListener(
      'keydown',
      (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        if (event.target instanceof Element && event.target.closest('.pdp-options__list')) {
          setTimeout(() => this.#onSelection(), 0);
        }
      },
      { signal }
    );
    document.addEventListener(
      'variant:update',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (String(detail?.data?.productId) === String(this.data.productId)) this.#onSelection();
      },
      { signal }
    );
    this.root.addEventListener(
      'quantity-selector:update',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (detail?.cartLine) return;
        this.qty = Number(detail?.quantity) || 1;
        this.justAdded = false;
        this.#render();
      },
      { signal }
    );
    document.addEventListener('cart:update', (event) => this.#onCart(/** @type {CustomEvent} */ (event)), { signal });

    this.ship?.querySelector('[data-fh-ship-act]')?.addEventListener('click', (event) => this.#onAction(event), { signal });
    this.addon?.querySelector('[data-fh-addon-select]')?.addEventListener('change', () => this.#syncAddon(), { signal });

    this.#syncAddon();
    this.#onSelection();
    this.#refreshCart();
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  /** @param {string} selector */
  #readJson(selector) {
    try {
      return JSON.parse(this.root.querySelector(selector)?.textContent || 'null');
    } catch {
      return null;
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return formatMoney(Math.round(cents), this.data.moneyFormat, currency);
  }

  get variant() {
    const input = this.root.querySelector('product-form-component input[name="id"]');
    const id = Number(input instanceof HTMLInputElement ? input.value : 0);
    return this.data.variants.find((v) => v.id === id) ?? this.data.variants[0];
  }

  get addonPrice() {
    const button = this.addon?.querySelector('[data-fh-addon-btn]');
    if (!this.cfg.addonOk || !(button instanceof HTMLElement)) return 0;
    return Number(button.dataset.price) || 0;
  }

  #onSelection() {
    const variant = this.variant;
    if (this.justAdded && variant && String(variant.id) !== String(this.addedVariantId)) this.justAdded = false;
    const pill = this.root.querySelector('[data-fh-free]');
    if (pill instanceof HTMLElement) pill.hidden = !(this.cfg.shipsFree || (variant && variant.price >= this.cfg.threshold));
    this.#syncLadder(variant);
    this.#render();
  }

  /** Carry every current selection to the other sizes; their PDP ignores options it doesn't have. */
  #syncLadder(variant) {
    if (!variant) return;
    const params = new URLSearchParams();
    for (const option of this.data.options) {
      const value = variant.options[option.index];
      const handle = option.values.find((entry) => entry.value === value)?.handle;
      if (handle) params.set(option.handle, handle);
    }
    const query = params.toString();
    for (const link of this.root.querySelectorAll('a[data-fh-ladder]')) {
      const base = (link.getAttribute('href') || '').split('?')[0];
      link.setAttribute('href', query ? `${base}?${query}` : base);
    }
  }

  #syncAddon() {
    const button = this.addon?.querySelector('[data-fh-addon-btn]');
    const select = this.addon?.querySelector('[data-fh-addon-select]');
    if (button instanceof HTMLElement && select instanceof HTMLSelectElement) {
      const option = select.selectedOptions[0];
      if (option) {
        button.dataset.variantId = option.value;
        button.dataset.price = option.dataset.price || '0';
        const price = button.querySelector('[data-fh-addon-price]');
        if (price) price.textContent = this.#money(Number(option.dataset.price) || 0);
      }
    }
    this.#render();
  }

  /** Same rules as snippets/fh-pdp-ship.liquid. */
  #plan() {
    const threshold = Number(this.cfg.threshold) || 0;
    const maxQty = Number(this.cfg.maxQty) || 0;
    const price = this.variant?.price ?? 0;
    const total = this.justAdded ? this.cart : this.cart + price * this.qty;
    if (this.cfg.shipsFree || total >= threshold) return { state: 'free', pct: 100, gap: 0, pick: null };
    const pct = threshold > 0 ? Math.min(100, Math.floor((total * 100) / threshold)) : 100;

    const gap = threshold - total;
    let more = null;
    if (!this.justAdded && price > 0) {
      const n = Math.ceil((threshold - this.cart) / price);
      if (n > this.qty && n <= maxQty) more = { act: 'qty', n, cost: (n - this.qty) * price };
    }
    let addon = null;
    const addonPrice = this.addonPrice;
    if (addonPrice > 0 && addonPrice >= gap) addon = { act: 'addon', n: 0, cost: addonPrice };
    const pick = more && addon ? (more.cost <= addon.cost ? more : addon) : more || addon;
    return { state: 'under', pct, gap, pick };
  }

  #render() {
    if (!this.ship) return;
    const plan = this.#plan();
    const text = this.cfg.text;
    let message = '';
    let label = '';
    let act = '';

    if (plan.state === 'free') {
      message = text.free;
    } else if (plan.pick?.act === 'qty') {
      message = text.more.replaceAll('[n]', String(plan.pick.n));
      label = text.moreButton.replaceAll('[n]', String(plan.pick.n));
      act = 'qty';
    } else if (plan.pick?.act === 'addon') {
      // no second Add button here: the stocking-stuffer card right below carries it (and its ribbon)
      const price = this.#money(this.addonPrice);
      message = text.addon.replaceAll('[item]', this.cfg.addonName).replaceAll('[price]', price);
      act = 'addon';
    } else {
      message = text.progress.replaceAll('[amount]', this.#money(plan.gap)).replaceAll('[flat]', this.cfg.flatRate);
    }

    this.ship.dataset.state = plan.state;
    const fill = this.ship.querySelector('[data-fh-ship-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${plan.pct}%`;
    const out = this.ship.querySelector('[data-fh-ship-text]');
    if (out && out.textContent !== message) out.textContent = message;
    const button = this.ship.querySelector('[data-fh-ship-act]');
    if (button instanceof HTMLButtonElement) {
      button.hidden = act !== 'qty';
      button.dataset.act = act;
      button.dataset.n = String(plan.pick?.n ?? 0);
      if (act === 'qty' && button.textContent !== label) button.textContent = label;
    }
    if (this.addon instanceof HTMLElement) this.addon.dataset.unlocks = String(act === 'addon');
  }

  /** @param {Event} event */
  #onAction(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset.act === 'qty') {
      const n = Number(button.dataset.n);
      const selector = /** @type {any} */ (this.root.querySelector('product-form-component quantity-selector-component'));
      if (selector && n > 0) {
        selector.setValue?.(String(n));
        selector.onQuantityChange?.();
        selector.updateButtonStates?.();
      }
      this.#track('pdp:fh_ship_more', { suggestedQuantity: n });
    }
  }

  /** @param {CustomEvent} event */
  #onCart(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.source === 'product-form-component' && !data.didError && String(data.productId) === String(this.data.productId)) {
      // this selection is in the cart now (sourceId is the added variant's id)
      this.justAdded = true;
      this.addedVariantId = detail.sourceId ?? this.variant?.id ?? null;
    }
    const total = detail.resource?.total_price;
    if (typeof total === 'number') {
      this.cart = total;
      this.#render();
    } else {
      this.#refreshCart();
    }
  }

  async #refreshCart() {
    try {
      // the theme declares `const Theme` in a classic script: a global binding, not a window property
      // eslint-disable-next-line no-undef
      const base = (typeof Theme !== 'undefined' && Theme?.routes?.cart_url) || '/cart';
      const response = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (typeof cart.total_price === 'number') {
        this.cart = cart.total_price;
        this.#render();
      }
    } catch {
      /* the helper keeps its server-rendered state */
    }
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} [extra]
   */
  #track(name, extra = {}) {
    const variant = this.variant;
    const detail = { product: this.data.productTitle, variantId: variant?.id ?? null, quantity: this.qty, ...extra };
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...detail });
  }
}

if (!customElements.get('fh-pdp-kit')) customElements.define('fh-pdp-kit', FhPdpKit);
